import { useCallback, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { precheck } from "@/lib/intake";
import { relPath } from "@/lib/dropFiles";

let seq = 0;

/** Uploads to an existing case: live progress, the server's per-file verdict, retry of failed files only (no duplicates). */
export function useCaseUploads(caseId, apply) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const rowsRef = useRef([]);
  const sync = useCallback((next) => { rowsRef.current = typeof next === "function" ? next(rowsRef.current) : next; setRows(rowsRef.current); }, []);
  const patch = useCallback((keys, p) => sync((rs) => rs.map((r) => (keys.includes(r.key) ? { ...r, ...p } : r))), [sync]);

  const run = useCallback(async (list) => {
    const files = list.filter((r) => !r.local);
    if (!files.length) return;
    const keys = files.map((r) => r.key);
    setBusy(true);
    const t0 = performance.now();
    let tUp = null;
    patch(keys, { status: "laster", pct: 0, error: null, timing: null });
    try {
      const fd = new FormData();
      files.forEach((r) => { fd.append("files", r.file); fd.append("paths", relPath(r.file)); });
      const r = await api.post(`/projects/${caseId}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total && e.loaded >= e.total) { tUp = tUp ?? performance.now(); patch(keys, { pct: 100, status: "tolkes" }); } else patch(keys, { pct: e.total ? Math.round((e.loaded / e.total) * 100) : null });
        },
      });
      const results = r.data.ingest?.results || [];
      const transfer = Math.round((tUp ?? performance.now()) - t0);
      const total = Math.round(performance.now() - t0);
      sync((rs) => rs.map((row) => {
        const i = keys.indexOf(row.key);
        if (i < 0) return row;
        const res = results[i];
        const timing = { transfer_ms: transfer, parse_ms: res?.ms ?? null, server_ms: r.data.timing?.total_ms ?? null, total_ms: total, reading: r.data.timing?.reading };
        return res ? { ...row, status: res.status, error: res.error || null, docs: res.docs, pct: 100, timing } : { ...row, status: "feil", error: "Serveren ga ikke noe svar for denne filen", timing };
      }));
      apply(r);
    } catch (e) {
      patch(keys, { status: "feil", pct: null, error: errMsg(e) });
    } finally {
      setBusy(false);
    }
  }, [caseId, apply, sync, patch]);

  const add = useCallback((list) => {
    const fresh = Array.from(list || []).map((file) => {
      const reason = precheck(file);
      return { key: `c${++seq}`, file, name: file.name, size: file.size, status: reason ? "feil" : "venter", pct: null, error: reason, local: Boolean(reason), attempt: 1 };
    });
    if (!fresh.length) return;
    sync((rs) => [...rs, ...fresh]);
    run(fresh);
  }, [run, sync]);

  const retry = useCallback((key) => {
    const row = rowsRef.current.find((r) => r.key === key);
    if (!row || busy) return;
    const next = { ...row, attempt: (row.attempt || 1) + 1 };
    sync((rs) => rs.map((r) => (r.key === key ? next : r)));
    run([next]);
  }, [run, busy, sync]);

  const dismiss = useCallback((key) => sync((rs) => rs.filter((r) => r.key !== key)), [sync]);
  const clear = useCallback(() => sync([]), [sync]);
  return { rows, busy, add, retry, dismiss, clear };
}
